/**
 * Tenant organization API client (paths under `ORGANIZATION_API`).
 *
 * | Route | Used in |
 * |-------|---------|
 * | fetch-tree | OrganizationManagement, OrganizationHierarchyManagement (when org API enabled; see `orgApiConfig.js`) |
 * | get | OrganizationWorkspace (edit open) |
 * | create | OrganizationManagement → OrganizationDetailsTab |
 * | create-with-customers | Call `createOrganizationWithCustomers` when you add a bulk UI |
 * | update/:id | OrganizationManagement, OrganizationHierarchy (when API enabled) |
 * | delete | OrganizationManagement |
 * | reparent | OrganizationManagement (parent change), OrganizationHierarchy |
 * | check-code | OrganizationDetailsTab (when API enabled) |
 * | customers/list | OrganizationManagement → Workspace (customers tab) |
 * | customers/create | Legacy simple create (still exported) |
 * | customers/create-with-portal-admin | OrganizationManagement → persistCustomer (new) |
 * | customers/update/:id | persistCustomer (edit) |
 * | customers/delete | persistDeleteCustomer |
 */
import api from "../axiosIntercepture";
import { ORGANIZATION_API } from "./organizationPaths";
import { assertApiSuccess, getApiErrorMessage } from "../../utils/apiErrorMessage";

const unwrapData = (res) => res?.data?.data ?? res?.data ?? res;

const unwrapError = (err) => getApiErrorMessage(err);

/**
 * POST { tenantId }
 * Expected: { data: { organizations: OrgFlatNode[] } } | { data: OrgFlatNode[] } | OrgFlatNode[]
 */
export async function fetchOrganizationTree(tenantId) {
  try {
    const res = await api.post(ORGANIZATION_API.FETCH_TREE, { tenantId });
    return unwrapData(res);
  } catch (err) {
    throw unwrapError(err);
  }
}

/**
 * POST { organizationId }
 */
export async function getOrganizationById(organizationId) {
  try {
    const res = await api.post(ORGANIZATION_API.GET_BY_ID, { organizationId });
    return unwrapData(res);
  } catch (err) {
    throw unwrapError(err);
  }
}

/**
 * POST create body (see `organizationApiExamples.js` / mappers).
 */
export async function createOrganization(payload) {
  try {
    const res = await api.post(ORGANIZATION_API.CREATE, payload);
    return unwrapData(res);
  } catch (err) {
    throw unwrapError(err);
  }
}

/**
 * POST { organization, customers: [...] } — optional composite for onboarding.
 */
export async function createOrganizationWithCustomers(payload) {
  try {
    const res = await api.post(ORGANIZATION_API.CREATE_WITH_CUSTOMERS, payload);
    return unwrapData(res);
  } catch (err) {
    throw unwrapError(err);
  }
}

/**
 * POST update body; URL includes organization id.
 */
export async function updateOrganization(organizationId, payload) {
  try {
    const res = await api.post(ORGANIZATION_API.UPDATE(organizationId), payload);
    return unwrapData(res);
  } catch (err) {
    throw unwrapError(err);
  }
}

/**
 * POST { organizationId }
 */
export async function deleteOrganization(organizationId) {
  try {
    const res = await api.post(ORGANIZATION_API.DELETE, { organizationId });
    return unwrapData(res);
  } catch (err) {
    throw unwrapError(err);
  }
}

/**
 * POST { organizationId, newParentOrganizationId }
 */
export async function reparentOrganization(organizationId, newParentOrganizationId) {
  try {
    const res = await api.post(ORGANIZATION_API.REPARENT, {
      organizationId,
      newParentOrganizationId,
    });
    return unwrapData(res);
  } catch (err) {
    throw unwrapError(err);
  }
}

/**
 * POST { tenantId, organizationCode, excludeOrganizationId? }
 */
export async function checkOrganizationCodeAvailable(tenantId, organizationCode, excludeOrganizationId) {
  try {
    const res = await api.post(ORGANIZATION_API.CHECK_CODE, {
      tenantId,
      organizationCode,
      excludeOrganizationId: excludeOrganizationId ?? undefined,
    });
    return unwrapData(res);
  } catch (err) {
    throw unwrapError(err);
  }
}

/**
 * POST { organizationId, page, pageSize, filters? }
 */
export async function listCustomersByOrganization(organizationId, page = 0, pageSize = 50, filters = {}) {
  try {
    const res = await api.post(ORGANIZATION_API.CUSTOMERS_LIST, {
      organizationId,
      page,
      pageSize,
      filters,
    });
    return unwrapData(res);
  } catch (err) {
    throw unwrapError(err);
  }
}

/**
 * POST { organizationId, ...customerFields }
 */
export async function createCustomerUnderOrganization(organizationId, customerPayload) {
  try {
    const res = await api.post(ORGANIZATION_API.CUSTOMERS_CREATE, {
      organizationId,
      ...customerPayload,
    });
    return unwrapData(res);
  } catch (err) {
    throw unwrapError(err);
  }
}

/**
 * POST { organizationId, ...customerFields, moduleAccess?, portalAdmin? }
 * Use for new customers from org workspace (includes portal admin / module access).
 */
export async function createCustomerWithPortalAdmin(organizationId, customerPayload) {
  try {
    const res = await api.post(ORGANIZATION_API.CUSTOMERS_CREATE_WITH_PORTAL_ADMIN, {
      organizationId,
      ...customerPayload,
    });
    assertApiSuccess(res, "Failed to create supplier");
    return unwrapData(res);
  } catch (err) {
    throw unwrapError(err);
  }
}

/**
 * POST body; customer id in path. Body should include organizationId for scoping.
 */
export async function updateCustomerUnderOrganization(customerId, customerPayload) {
  try {
    const res = await api.post(ORGANIZATION_API.CUSTOMERS_UPDATE(customerId), customerPayload);
    return unwrapData(res);
  } catch (err) {
    throw unwrapError(err);
  }
}

/**
 * POST { organizationId, customerId }
 */
export async function deleteCustomerUnderOrganization(organizationId, customerId) {
  try {
    const res = await api.post(ORGANIZATION_API.CUSTOMERS_DELETE, { organizationId, customerId });
    return unwrapData(res);
  } catch (err) {
    throw unwrapError(err);
  }
}
